// Portfolio Administration v1.0 PR 4a §7.6 — per-item drawer.

import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Button, Badge, Textarea } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/api/client";
import { useCompleteCalendarItem } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { ResourceDrawer } from "../common/ResourceDrawer";
import { SnoozeItemModal } from "./SnoozeItemModal";
import {
  daysLabel,
  daysUntil,
  calendarDate,
  formatDate,
} from "../common/colorBand";
import { categorize, DUE_DATE_CATEGORIES } from "./categories";
import type { ComplianceCalendarItemDto } from "@/api/portfolioAdmin/types";

interface DueDateItemDrawerProps {
  loanId: string;
  item: ComplianceCalendarItemDto;
  open: boolean;
  onClose: () => void;
}

export function DueDateItemDrawer({
  loanId,
  item,
  open,
  onClose,
}: DueDateItemDrawerProps) {
  const toast = useToast();
  const auth = useAuth();
  const canWaive = auth.hasRoleCode("E1") || auth.hasRoleCode("ADMIN");

  const complete = useCompleteCalendarItem(loanId);
  const [notes, setNotes] = useState("");
  const [snoozeOpen, setSnoozeOpen] = useState(false);

  const days = daysUntil(item.dueDate);
  const isDone = item.status === "Completed" || item.status === "Waived";
  const categoryLabel =
    DUE_DATE_CATEGORIES.find((c) => c.key === categorize(item.sourceType))?.label ??
    "Other";

  const onComplete = async () => {
    try {
      await complete.mutateAsync({
        itemId: item.id,
        body: { notes: notes || null },
      });
      toast.success("Item marked complete.");
      setNotes("");
      onClose();
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not mark complete.",
      );
    }
  };

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      title={item.title}
      subtitle={`${categoryLabel} · Due ${calendarDate(item.dueDate)} (${daysLabel(days)})`}
      testId="due-dates-drawer"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          {!isDone && (
            <>
              <Button
                variant="secondary"
                onClick={() => setSnoozeOpen(true)}
                data-testid="due-dates-snooze-button"
              >
                Snooze
              </Button>
              {canWaive && (
                <Button
                  variant="secondary"
                  disabled
                  title="Waive endpoint ships in PR 4b alongside the failure-tracking integration."
                  data-testid="due-dates-waive-button"
                >
                  Waive
                </Button>
              )}
              <Button
                variant="primary"
                onClick={() => void onComplete()}
                disabled={complete.isPending}
                data-testid="due-dates-complete-button"
              >
                Mark complete
              </Button>
            </>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-space-4" data-testid="due-dates-drawer-body">
        <section>
          <dl className="grid grid-cols-2 gap-y-space-1 text-small m-0">
            <dt className="text-caption text-gray-mid uppercase tracking-label">Status</dt>
            <dd className="m-0">
              <Badge
                status={
                  item.status === "Completed" ? "finalized" :
                  item.status === "Waived" ? "archived" :
                  (days ?? 1) < 0 ? "error" : "draft"
                }
              >
                {item.status}
              </Badge>
            </dd>
            <dt className="text-caption text-gray-mid uppercase tracking-label">Severity</dt>
            <dd className="m-0">{item.severity}</dd>
            <dt className="text-caption text-gray-mid uppercase tracking-label">Source type</dt>
            <dd className="m-0 font-mono text-caption">{item.sourceType}</dd>
            <dt className="text-caption text-gray-mid uppercase tracking-label">Owner</dt>
            <dd className="m-0">{item.assignedToUserId ?? "Unassigned"}</dd>
            {item.completedDate && (
              <>
                <dt className="text-caption text-gray-mid uppercase tracking-label">Completed</dt>
                <dd className="m-0">{formatDate(item.completedDate)}</dd>
              </>
            )}
          </dl>
        </section>

        {item.description && (
          <section>
            <h3 className="font-display font-semibold text-h4 m-0 mb-space-1">
              Description
            </h3>
            <p className="text-small text-onyx m-0 whitespace-pre-wrap">
              {item.description}
            </p>
          </section>
        )}

        {item.sourceEntityId && (
          <section className="rounded-md border border-border-light p-space-2">
            <p className="text-caption text-gray-mid m-0">
              Linked source:{" "}
              <span className="font-mono">{item.sourceEntityType}</span> ·{" "}
              <span className="font-mono">{item.sourceEntityId}</span>
            </p>
          </section>
        )}

        {!isDone && (
          <section>
            <label htmlFor="due-dates-notes" className="block text-caption text-gray-mid uppercase tracking-label mb-space-1">
              Notes (optional, attached to Mark complete)
            </label>
            <Textarea
              id="due-dates-notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="due-dates-notes"
            />
          </section>
        )}

        <section className="rounded-md bg-white-warm p-space-2">
          <div className="flex items-start gap-space-2">
            <AlertTriangle size={14} className="text-gray-mid shrink-0 mt-[2px]" aria-hidden />
            <p className="text-caption text-gray-mid m-0">
              Reassign and Waive endpoints ship in PR 4b alongside the
              failure-tracking integration. Snooze + Mark complete are wired
              today. Repeated snoozes (&gt;2) auto-raise an E2 ticket — see
              the Activity tab.
            </p>
          </div>
        </section>
      </div>

      {snoozeOpen && (
        <SnoozeItemModal
          loanId={loanId}
          item={item}
          open={snoozeOpen}
          onClose={() => {
            setSnoozeOpen(false);
            onClose();
          }}
        />
      )}
    </ResourceDrawer>
  );
}
